import Card from "@/components/Card";
import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonCard from "@/components/SkeletonCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { listLokasiKuliah } from "@/constants";
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import {
  getListDataPilihanProdi,
  getListProdiPerPeriode,
} from "@/services/getData";
import {
  DataPilihanProdi,
  KonsentrasiProdi,
  LokasiKuliah,
  SysProgramStudi,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Info,
  Loader2,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ListProdi from "./partial/ListProdi";

// --- TYPES & API (TIDAK DIUBAH) ---
type ProdiSelection = {
  uk_id: string;
  jenjang: string;
  pilihan_lokasi_kuliah: LokasiKuliah | null;
  pilihan_prodi_ke: number;
  is_vokasi: boolean;
  kelas_kuliah: LokasiKuliah[];
  konsentrasi_prodi?: string;
};

type PilihanProdiState = {
  id_data_pendaftaran: string;
  jumlah_pilihan_prodi: string;
  list_prodi: ProdiSelection[];
};

type OptionProdi = {
  label: string;
  value: string;
  unitkerjaGroupKey: string;
  jenjang: string;
  isActive: boolean;
  kelasKuliah: LokasiKuliah[];
  isVokasi: boolean;
  konsentrasiProdi: KonsentrasiProdi[];
};

const saveProdi = async (form: PilihanProdiState) => {
  const response = await api.patch(
    "/siswa/penerimaan/smbjm/pilihan-prodi/update",
    form,
    { withCredentials: true },
  );
  return response.data;
};

// --- COMPONENT ---
export default function Prodi({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const { user } = useUser();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { isPendingPeriode, timePassedActive } = useTimePeriode();

  const [form, setForm] = useState<PilihanProdiState>({
    id_data_pendaftaran: idDataPendaftaran,
    jumlah_pilihan_prodi: "",
    list_prodi: [],
  });

  const [openListProdi, setOpenListProdi] = useState(
    Array(Number(form.jumlah_pilihan_prodi)).fill(false),
  );
  const [openKonsentrasiProdi, setOpenKonsentrasiProdi] = useState(false);
  const [selectedProdi, setSelectedProdi] = useState<OptionProdi | null>(null);

  const { data: dataProdi, isPending: isPendingDataProdi } = useQuery({
    queryKey: ["listProdiProfesi"],
    queryFn: () => getListProdiPerPeriode(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const { isValidPendaftaran } = useDataPendaftaran();

  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () => getListDataPilihanProdi(sessionPeriode?.id_sys_periode),
  });

  const optionsProdi = dataProdi?.list_prodi?.map(
    (sysProdi: SysProgramStudi) => {
      return {
        label: sysProdi?.unitkerja?.nama,
        value: `${sysProdi?.uk_id}`,
        unitkerjaGroupKey: sysProdi?.unitkerja?.unitkerja_group_key,
        jenjang: sysProdi?.unitkerja?.uk_program,
        isActive: sysProdi?.unitkerja?.is_active,
        kelasKuliah: sysProdi?.unitkerja?.kelas_kuliah,
        isVokasi: sysProdi?.unitkerja?.is_vokasi,
        konsentrasiProdi: sysProdi.unitkerja?.konsentrasi_prodi,
      };
    },
  );

  const filteredDataProdi = optionsProdi?.filter((option: OptionProdi) => {
    return option?.unitkerjaGroupKey == "5" && option.isActive;
  });

  const optionsKonsentrasiProdi = selectedProdi
    ? selectedProdi?.konsentrasiProdi?.map((item: KonsentrasiProdi) => {
        return {
          label: item.nama_konsentrasi,
          value: `${item.id_ref_konsentrasi_prodi}`,
        };
      })
    : [];

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingSaveProdi,
    error,
  } = useMutation({
    mutationFn: saveProdi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      });
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  useEffect(() => {
    if (!dataPilihanProdi) return;

    setForm((prev) => ({
      ...prev,
      jumlah_pilihan_prodi: `${
        dataPilihanProdi?.length === 0 ? 1 : dataPilihanProdi.length
      }`,
      list_prodi: dataPilihanProdi?.map((item: DataPilihanProdi) => {
        return {
          uk_id: `${item?.uk_id}`,
          jenjang: item.unitkerja?.uk_program,
          pilihan_lokasi_kuliah: item.pilihan_lokasi_kuliah,
          pilihan_prodi_ke: item.urutan_program_studi,
          is_vokasi: item.unitkerja?.is_vokasi,
          kelas_kuliah: item.unitkerja?.kelas_kuliah,
          konsentrasi_prodi: item.id_ref_konsentrasi_prodi,
        };
      }),
    }));

    dataPilihanProdi?.map((item: DataPilihanProdi) => {
      const selected: OptionProdi = {
        label: item.unitkerja?.nama || "",
        value: String(item.uk_id) || "",
        isActive: item.unitkerja?.is_active ?? false,
        isVokasi: item.unitkerja?.is_vokasi ?? false,
        jenjang: item.unitkerja?.uk_program || "",
        kelasKuliah: item.unitkerja?.kelas_kuliah || [],
        konsentrasiProdi: item.unitkerja?.konsentrasi_prodi || [],
        unitkerjaGroupKey: item.unitkerja?.unitkerja_group_key || "",
      };

      if (selected.value) setSelectedProdi(selected);
    });
  }, [dataPilihanProdi]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* --- BAGIAN KIRI: INFORMASI PRODI --- */}
      <div className="w-full lg:w-1/2 space-y-4 lg:sticky lg:top-4 h-fit">
        <Card className="border-none shadow-md overflow-hidden bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800">
          <div className="bg-slate-50 dark:bg-slate-900/50 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-sm uppercase tracking-wide text-slate-700 dark:text-slate-300">
              Referensi Prodi
            </h3>
          </div>
          <div className="p-0">
            <ListProdi dataProdi={dataProdi} isPending={isPendingDataProdi} />
          </div>
        </Card>

        {/* Info Box Tambahan */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 flex gap-3 items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Catatan Penting:</p>
            <p className="leading-relaxed opacity-90 text-xs">
              Pastikan Anda memilih Konsentrasi dan Lokasi Kuliah jika tersedia.
              Data yang sudah divalidasi tidak dapat diubah.
            </p>
          </div>
        </div>
      </div>

      {/* --- BAGIAN KANAN: FORMULIR --- */}
      <div className="w-full lg:w-1/2">
        {isPendingDataProdi || isPendingPeriode ? (
          <SkeletonCard />
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            {/* Header Form */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Pilihan Program Studi
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Tentukan program studi tujuan Anda.
                  </p>
                </div>
              </div>
              {isValidPendaftaran && (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Terverifikasi
                </Badge>
              )}
            </div>

            {/* Content Form */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Loop Inputs */}
                <div className="space-y-6">
                  {form.jumlah_pilihan_prodi &&
                    Array(Number(form.jumlah_pilihan_prodi))
                      .fill(null)
                      .map((_, index) => {
                        return (
                          <div
                            key={index}
                            className="group relative flex flex-col gap-4 p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-300"
                          >
                            <div className="absolute -top-3 left-4">
                              <Badge className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 shadow-sm hover:bg-white">
                                Pilihan {index + 1}
                              </Badge>
                            </div>

                            {/* 1. Input Prodi */}
                            <div className="space-y-1.5 mt-2">
                              <Label className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 ml-1">
                                Program Studi
                              </Label>
                              <Combobox
                                open={openListProdi[index]}
                                setOpen={(isOpen) =>
                                  setOpenListProdi((prevOpenListProdi) => {
                                    const newOpenList = [...prevOpenListProdi];
                                    newOpenList[index] = isOpen;
                                    return newOpenList;
                                  })
                                }
                                value={form.list_prodi[index]?.uk_id || ""}
                                setValue={(value) => {
                                  const selected: OptionProdi =
                                    filteredDataProdi?.find(
                                      (item: OptionProdi) =>
                                        item.value === value,
                                    );
                                  setSelectedProdi(selected);

                                  const isDuplicate =
                                    form.list_prodi.length > 0
                                      ? form.list_prodi.some(
                                          (prodi) => prodi?.uk_id === value,
                                        )
                                      : false;

                                  if (isDuplicate) {
                                    toast.error("Prodi ini sudah Anda pilih.");
                                    return;
                                  }

                                  setForm((prev) => {
                                    const newListProdi = [...prev.list_prodi];
                                    newListProdi[index] = {
                                      uk_id: value,
                                      jenjang: selected?.jenjang,
                                      pilihan_lokasi_kuliah: null,
                                      pilihan_prodi_ke: index + 1,
                                      is_vokasi: selected?.isVokasi,
                                      kelas_kuliah: selected?.kelasKuliah,
                                    };
                                    return {
                                      ...prev,
                                      list_prodi: newListProdi,
                                    };
                                  });
                                }}
                                options={filteredDataProdi}
                                placeholder="Cari program studi..."
                              />
                            </div>

                            {/* 2. Input Konsentrasi (Kondisional) */}
                            {selectedProdi &&
                            selectedProdi?.konsentrasiProdi?.length > 0 ? (
                              <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                                <Label className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 ml-1">
                                  Konsentrasi
                                </Label>
                                <Combobox
                                  open={openKonsentrasiProdi}
                                  setOpen={setOpenKonsentrasiProdi}
                                  value={
                                    form.list_prodi[index]?.konsentrasi_prodi ||
                                    ""
                                  }
                                  setValue={(value) => {
                                    setForm((prev) => {
                                      const newListProdi = [...prev.list_prodi];
                                      newListProdi[index] = {
                                        ...newListProdi[index],
                                        konsentrasi_prodi: value,
                                      };
                                      return {
                                        ...prev,
                                        list_prodi: newListProdi,
                                      };
                                    });
                                  }}
                                  options={optionsKonsentrasiProdi}
                                  placeholder="Pilih konsentrasi..."
                                />
                                {error?.response?.data?.errors?.[
                                  `list_prodi.${index}.konsentrasi_prodi`
                                ] && (
                                  <InputError text="Konsentrasi wajib dipilih" />
                                )}
                              </div>
                            ) : null}

                            {/* 3. Input Lokasi (Kondisional) */}
                            {form.list_prodi[index]?.kelas_kuliah &&
                            form.list_prodi[index]?.kelas_kuliah.length > 1 ? (
                              <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                                <Label className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 ml-1">
                                  Lokasi Kuliah
                                </Label>
                                <Select
                                  value={
                                    form.list_prodi[index]
                                      ?.pilihan_lokasi_kuliah || ""
                                  }
                                  onValueChange={(value) => {
                                    setForm((prev) => {
                                      const newListProdi = [...prev.list_prodi];
                                      newListProdi[index] = {
                                        ...newListProdi[index],
                                        pilihan_lokasi_kuliah:
                                          value.toLowerCase() as LokasiKuliah,
                                      };
                                      return {
                                        ...prev,
                                        list_prodi: newListProdi,
                                      };
                                    });
                                  }}
                                >
                                  <SelectTrigger className="bg-white dark:bg-slate-900 border-slate-200">
                                    <SelectValue placeholder="Pilih Lokasi Kuliah">
                                      {form.list_prodi[index]
                                        ?.pilihan_lokasi_kuliah &&
                                        listLokasiKuliah.find(
                                          (loc) =>
                                            loc.toLowerCase() ===
                                            form.list_prodi[index]
                                              ?.pilihan_lokasi_kuliah,
                                        )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {listLokasiKuliah.map((value, idx) => (
                                      <SelectItem
                                        key={idx}
                                        value={value.toLowerCase()}
                                      >
                                        {value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {error?.response?.data?.errors?.[
                                  `list_prodi.${index}.pilihan_lokasi_kuliah`
                                ] && (
                                  <InputError text="Lokasi kuliah wajib diisi" />
                                )}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                </div>

                {/* Global Error Alert */}
                {error && error?.response?.data?.message && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Gagal Menyimpan</AlertTitle>
                    <AlertDescription>
                      {error?.response?.data?.message}
                    </AlertDescription>
                  </Alert>
                )}

                <Separator />

                {/* Actions */}
                <div className="flex justify-end pt-2">
                  {!isValidPendaftaran &&
                  !timePassedActive?.waktu_selesai_daftar_valid_selesai ? (
                    <Button
                      type="submit"
                      disabled={isPendingSaveProdi}
                      className="min-w-[150px]"
                    >
                      {isPendingSaveProdi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Menyimpan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Simpan Pilihan
                        </>
                      )}
                    </Button>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      Formulir terkunci atau masa pendaftaran telah berakhir.
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
